import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import ConfirmationModal from "../../../ui-components/confirmation-modal/confirmationModal";
import Button, { ButtonThemes } from "../../../ui-components/button/button";
import { StepProps } from "../../../app/data/common/models";
import {
  requestRatesSelector,
  addShippingItem,
  updateShippingItem,
  deleteShippingItem,
  clearDeletedShippingItems,
  setNeedInsurance
} from "../../../slices/rates/requestRatesSlice";
import XGSCheckbox from "../../../ui-components/xgs-checkbox/xgsCheckbox";
import ShippingItemForm from "./shippingItemForm";
import { commonSelector } from "../../../slices/common/commonSlice";

const ShippingItems: React.FC<StepProps> = (props) => {
  const dispatch = useDispatch();
  const { next, previous } = props;
  const requestRatesState = useSelector(requestRatesSelector);
  const commonState = useSelector(commonSelector);
  const [showAddShippingItemForm, setAddShippingItemForm] = useState<boolean>(false);
  const [removeItemIndex, setRemoveItemIndex] = useState<number | undefined>(undefined);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState<boolean>(false);
  const [editShippingItemIndex, setEditShippingItemIndex] = useState<number | undefined>(undefined);
  const [needInsuranceCheckbox, setNeedInsuranceCheckbox] = useState<boolean>(false);

  const onClickBack = () => {
    dispatch(clearDeletedShippingItems());
    dispatch(setNeedInsurance(needInsuranceCheckbox));
    previous && previous();
  }
;
  const onClickNext = () => {
    dispatch(clearDeletedShippingItems());
    dispatch(setNeedInsurance(needInsuranceCheckbox));
    next && next();
  };

  const getClassLabel = (freightClassValue: string) => {
    let freightClass = commonState.freightClasses.find(obj => obj.value === freightClassValue);
    return freightClass ? freightClass.label : freightClassValue;
  };

  useEffect(() => {
    setNeedInsuranceCheckbox(requestRatesState.needInsurance);
  }, [requestRatesState.needInsurance]);

  return (
    <div className="xgs-request-rates__shipping-items">
      <div className="xgs-request-rates__step__header">List all items you are shipping</div>
      <div className="xgs-request-rates__insurance">
        <XGSCheckbox
          name="needInsurance"
          value="true"
          onChange={() => setNeedInsuranceCheckbox(!needInsuranceCheckbox)}
          checked={needInsuranceCheckbox}
        >
          Excess value liability required
        </XGSCheckbox>
      </div>
      {requestRatesState.items.map((item, index) => (
        <div key={index}>
          {(index === editShippingItemIndex) && (
            <ShippingItemForm
              itemIndex={index}
              onSubmit={(data) => {
                dispatch(updateShippingItem({ index, data }));
                setEditShippingItemIndex(undefined);
              }}
              onCancel={() => {
                setEditShippingItemIndex(undefined);
              }}
              needInsurance={needInsuranceCheckbox}
            />
          )}
          {(editShippingItemIndex === undefined || (index !== editShippingItemIndex)) && !item.deleted && (
            <div className="xgs-request-rates__shipping-items__item">
              <div className="xgs-request-rates__shipping-items__item__columns">
                <div>
                  {item.rolls && <div className="xgs-request-rates__shipping-items__item__property">Rolls</div> }
                  <div className="xgs-request-rates__shipping-items__item__property">{item.pieces} piece{(item.pieces && item.pieces > 1) ? "s" : ""}, 
                  {item.yards ? " " + item.yards + " yards, " : ""} {item.weight} pounds</div>
                  <div className="xgs-request-rates__shipping-items__item__property">Class: {getClassLabel(item.freightClass)}</div>
                  {item.description && <div className="xgs-request-rates__shipping-items__item__property">Description: {item.description}</div>}
                  {needInsuranceCheckbox && item.declaredValue && <div className="xgs-request-rates__shipping-items__item__property">Declared Value: {item.declaredValue}</div>}
                </div>
                <div>
                  <div className="xgs-request-rates__shipping-items__item__action">
                    <span
                      className="xgs-request-rates__shipping-items__link"
                      onClick={() => {
                        setAddShippingItemForm(false);
                        setEditShippingItemIndex(index);
                      }}
                    >
                      Edit
                    </span>
                  </div>
                  <div className="xgs-request-rates__shipping-items__item__action">
                    <span
                      className="xgs-request-rates__shipping-items__link"
                      onClick={() => {
                        setRemoveItemIndex(index);
                        setShowRemoveConfirmation(true);
                      }}
                    >
                      Remove
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
      {(showAddShippingItemForm || requestRatesState.items.length === 0) &&
        <ShippingItemForm
          onSubmit={(data) => {
            dispatch(addShippingItem(data));
            setAddShippingItemForm(false);
          }}
          needInsurance={needInsuranceCheckbox}
        />
      }
      {!showAddShippingItemForm && (editShippingItemIndex === undefined) && (requestRatesState.items.length > 0) && (requestRatesState.items.filter(item => !item.deleted).length < 15) && (
        <div className="xgs-request-rates__shipping-items__add">
          <span
            className="xgs-request-rates__shipping-items__link"
            onClick={() => { setAddShippingItemForm(true); }}
          >
            + Add new item
          </span>
        </div>
      )}
      <div className="xgs-request-rates__shipping-items__notes xgs-request-rates__shipping-items__notes--guided">
        <strong>Note:</strong> You can add up to 15 items in a Rate Quote request. However, if you add more than 10 items, you will not be able to create a BOL based on that Rate Quote.
      </div>
      <div style={{ marginTop: 34 }}>
        <Button
          theme={ButtonThemes.gray}
          onClick={onClickBack}
          className="xgs-request-rates__step__nav-button"
        >
          Back
        </Button>
        <Button
          theme={ButtonThemes.blue}
          onClick={onClickNext}
          className="xgs-request-rates__step__nav-button"
          disabled={requestRatesState.items.length === 0 || requestRatesState.items.filter(item => !item.deleted).length === 0 || (editShippingItemIndex !== undefined)}
        >
          Get rates
        </Button>
      </div>
      <ConfirmationModal
        opened={showRemoveConfirmation}
        confirmButtonText="Remove"
        onCancel={() => {
          setShowRemoveConfirmation(false);
        }}
        onConfirm={() => {
          dispatch(deleteShippingItem(removeItemIndex));
          setShowRemoveConfirmation(false);
          setRemoveItemIndex(undefined);
        }}>
        The shipping item will be removed from the list.
      </ConfirmationModal>
    </div>
  );
};

export default ShippingItems;
